import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const allImages = [
  // 4:3 photos first — used for preview
  "/assets/gallery/wa5.jpeg",
  "/assets/gallery/wa1.jpeg",
  "/assets/gallery/IMG_7367.JPG",
  "/assets/gallery/wa4.jpeg",
  // remaining normal
  "/assets/gallery/3.jpg",
  "/assets/gallery/5.jpg",
  "/assets/gallery/2.jpg",
  "/assets/gallery/6.jpg",
  // wide/panoramic last
  "/assets/gallery/wa2.jpeg",
  "/assets/gallery/wa3.jpeg",
];

const PREVIEW_COUNT = 6;

const IconBtn: React.FC<{ onClick: (e: React.MouseEvent) => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 44, height: 44,
      display: "flex", alignItems: "center", justifyContent: "center",
      borderRadius: "50%",
      border: "1px solid rgba(255,255,255,0.25)",
      color: "rgba(255,255,255,0.7)",
      cursor: "pointer",
      userSelect: "none",
      transition: "border-color 0.2s, color 0.2s",
      "&:hover": { borderColor: "#c8b87a", color: "#c8b87a" },
    }}
  >
    {children}
  </Box>
);

const GalerieSection: React.FC = () => {
  const [showAll, setShowAll] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() =>
    setLightbox((i) => (i === null ? null : (i - 1 + allImages.length) % allImages.length)), []);
  const next = useCallback(() =>
    setLightbox((i) => (i === null ? null : (i + 1) % allImages.length)), []);

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, closeLightbox, prev, next]);

  // Escape from full gallery page
  useEffect(() => {
    if (!showAll || lightbox !== null) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setShowAll(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showAll, lightbox]);

  useEffect(() => {
    document.body.style.overflow = (showAll || lightbox !== null) ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showAll, lightbox]);

  const MasonryGrid: React.FC<{ images: string[]; onClickImage: (i: number) => void }> = ({ images, onClickImage }) => (
    <Box sx={{ columnCount: { xs: 2, sm: 2, md: 3 }, columnGap: "10px" }}>
      {images.map((src, i) => (
        <Box
          key={src}
          onClick={() => onClickImage(i)}
          sx={{ display: "block", mb: "10px", borderRadius: "8px", overflow: "hidden", breakInside: "avoid", cursor: "zoom-in" }}
        >
          <Box
            component="img"
            src={src}
            alt={`Statek Vítek ${i + 1}`}
            sx={{ width: "100%", display: "block", transition: "transform 0.4s ease", "&:hover": { transform: "scale(1.04)" } }}
          />
        </Box>
      ))}
    </Box>
  );

  const Lightbox = () => lightbox === null ? null : (
    <Box
      onClick={closeLightbox}
      sx={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.93)", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={{ position: "absolute", top: 20, right: 24 }}>
        <IconBtn onClick={closeLightbox}><CloseIcon sx={{ fontSize: 18 }} /></IconBtn>
      </Box>
      <Box sx={{ position: "absolute", left: { xs: 8, md: 24 } }}>
        <IconBtn onClick={(e) => { e.stopPropagation(); prev(); }}><ArrowBackIosNewIcon sx={{ fontSize: 16 }} /></IconBtn>
      </Box>
      <Box
        component="img"
        src={allImages[lightbox]}
        alt={`Statek Vítek ${lightbox + 1}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{ maxWidth: { xs: "88vw", md: "78vw" }, maxHeight: "88vh", objectFit: "contain", borderRadius: "6px", boxShadow: "0 8px 48px rgba(0,0,0,0.6)" }}
      />
      <Box sx={{ position: "absolute", right: { xs: 8, md: 24 } }}>
        <IconBtn onClick={(e) => { e.stopPropagation(); next(); }}><ArrowForwardIosIcon sx={{ fontSize: 16 }} /></IconBtn>
      </Box>
      <Typography sx={{ position: "absolute", bottom: 20, color: "rgba(255,255,255,0.35)", fontSize: "13px", letterSpacing: "2px" }}>
        {lightbox + 1} / {allImages.length}
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Main section */}
      <Box id="foto" sx={{ background: "#f5f0e5" }}>
        <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 6 }, maxWidth: "950px", mx: "auto" }}>
          <Typography sx={{ fontSize: "13px", letterSpacing: "4px", color: "#a89858", textTransform: "uppercase", mb: 1 }}>
            Fotogalerie
          </Typography>
          <Typography variant="h4" component="h2" sx={{ color: "#2d2a22", fontWeight: "bold", mb: 3 }}>
            Ze statku
          </Typography>

          <MasonryGrid images={allImages.slice(0, PREVIEW_COUNT)} onClickImage={(i) => setLightbox(i)} />

          {/* CTA */}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Box
              onClick={() => setShowAll(true)}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 1, cursor: "pointer",
                color: "#2d2a22", borderBottom: "1px solid #2d2a22", pb: "2px",
                transition: "color 0.2s, border-color 0.2s",
                "&:hover": { color: "#a89858", borderColor: "#a89858" },
              }}
            >
              <Typography sx={{ fontSize: "13px", letterSpacing: "3px", textTransform: "uppercase", fontWeight: 700 }}>
                Zobrazit více
              </Typography>
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Full gallery page overlay */}
      {showAll && (
        <Box
          sx={{
            position: "fixed", inset: 0, zIndex: 1300,
            background: "#f5f0e5", overflowY: "auto",
          }}
        >
          <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 6 }, maxWidth: "950px", mx: "auto" }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
              <Box
                onClick={() => setShowAll(false)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1, cursor: "pointer",
                  color: "#2d2a22", transition: "color 0.2s",
                  "&:hover": { color: "#a89858" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase", fontWeight: 700 }}>
                  Zpět
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Typography variant="h5" component="h2" sx={{ color: "#2d2a22", fontWeight: 700 }}>
                Všechny fotografie
              </Typography>
            </Box>

            <MasonryGrid images={allImages} onClickImage={(i) => setLightbox(i)} />
          </Box>
        </Box>
      )}

      <Lightbox />
    </>
  );
};

export default GalerieSection;
